import { useEffect, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CourseCard } from "@/components/site/CourseCard";
import { PageHero, Empty, Spinner } from "@/components/site/Bits";
import { publicApi } from "@/lib/api";
import { usePageTitle } from "@/lib/site";

const PRICES = { all: [null, null], "0-20": [0, 20], "20-50": [20, 50], "50+": [50, null] };
const INIT = { q: "", category: "all", price: "all", date_from: "", date_to: "" };

export default function Courses({ upcoming = false }) {
  usePageTitle(upcoming ? "الدورات القادمة" : "جميع الدورات");
  const [f, setF] = useState(INIT);
  const [data, setData] = useState({ courses: null, categories: [] });
  useEffect(() => {
    const [min_price, max_price] = PRICES[f.price];
    const params = { upcoming, q: f.q || undefined, category: f.category === "all" ? undefined : f.category,
      min_price: min_price ?? undefined, max_price: max_price ?? undefined, date_from: f.date_from || undefined, date_to: f.date_to || undefined };
    const t = setTimeout(() => publicApi.get("/courses", { params }).then((r) => setData(r.data)), 250);
    return () => clearTimeout(t);
  }, [f, upcoming]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));
  const dirty = JSON.stringify(f) !== JSON.stringify(INIT);
  return (
    <div data-testid={upcoming ? "upcoming-page" : "courses-page"}>
      <PageHero eyebrow="دليل الدورات" title={upcoming ? "الدورات القادمة" : "جميع الدورات"} subtitle="اختر الدورة المناسبة لك واحجز مقعدك قبل اكتمال العدد. جميع الأسعار بالريال العماني." />
      <div className="container-x py-12">
        <div className="card-lux !transform-none p-4 md:p-5 grid gap-3 md:grid-cols-12 items-end mb-10" data-testid="courses-filters">
          <div className="md:col-span-4 relative">
            <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--muted-ink)]" />
            <Input value={f.q} onChange={set("q")} placeholder="ابحث باسم الدورة..." className="pr-10 h-11" data-testid="courses-search-input" />
          </div>
          <div className="md:col-span-2">
            <Select value={f.category} onValueChange={set("category")} dir="rtl">
              <SelectTrigger className="h-11" data-testid="courses-category-select"><SelectValue placeholder="المجال" /></SelectTrigger>
              <SelectContent><SelectItem value="all">كل المجالات</SelectItem>{data.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select value={f.price} onValueChange={set("price")} dir="rtl">
              <SelectTrigger className="h-11" data-testid="courses-price-select"><SelectValue placeholder="السعر" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">كل الأسعار</SelectItem><SelectItem value="0-20">حتى 20 ر.ع.</SelectItem>
                <SelectItem value="20-50">20 – 50 ر.ع.</SelectItem><SelectItem value="50+">أكثر من 50 ر.ع.</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="md:col-span-2 text-xs text-[var(--muted-ink)]">من تاريخ<Input type="date" value={f.date_from} onChange={set("date_from")} className="h-11 mt-1" data-testid="courses-date-from" /></label>
          <label className="md:col-span-2 text-xs text-[var(--muted-ink)]">إلى تاريخ<Input type="date" value={f.date_to} onChange={set("date_to")} className="h-11 mt-1" data-testid="courses-date-to" /></label>
          {dirty && <button onClick={() => setF(INIT)} className="md:col-span-12 text-sm text-[var(--copper)] inline-flex items-center gap-1 justify-self-start" data-testid="courses-clear-filters"><X size={14} /> مسح التصفية</button>}
        </div>
        {!data.courses ? <Spinner /> : data.courses.length ? (
          <div className="grid gap-7 md:grid-cols-2 lg:grid-cols-3" data-testid="courses-grid">{data.courses.map((c, i) => <CourseCard key={c.id} course={c} index={i} />)}</div>
        ) : <Empty text="لا توجد دورات مطابقة لبحثك" testId="courses-empty" />}
      </div>
    </div>
  );
}
